<template>
  <div>
    <!-- Add Filter Bar-->
    <!-- Add Course Filter dropdown with All options included-->
    <!-- Add date filter dropdown-->

    <!-- Add bar chart to show concluded and pending users general (filtered by course) -->
    <!-- Add bar chart to show concluded and pending users classified by departments -->
    <!-- Show table with pending users only (small table) -->
    <!-- Add bar chart to show succeed and failed exams  -->
    <b-card no-body>
      <b-row class="mx-1 mt-1">
        <b-col cols="2">
          <div class="mb-2">
            <label
              style="font-size: 16px;"
              for="dateSelectId"
              class="form-label"
              >{{ $t("Date") }}</label
            >
            <v-select
              id="dateSelectId"
              :reduce="(val) => val.id"
              v-model="selectedDateFilterId"
              label="title"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="dateFilterOptions"
              class="select-size-lg"
            />
          </div>
        </b-col>
        <b-col cols="2">
          <label
            style="font-size: 16px;"
            for="courseSelectId"
            class="form-label"
            >{{ $t("Courses") }}</label
          >
          <CoursePicker @selectionChanged="courseSelectionChanged" />
        </b-col>
        <b-col v-if="selectedDateFilterId === 4" md="2" class="mt-1">
          <b-form-group label-for="example-datepicker" label="Start date">
            <flat-pickr
              v-model="selectedStartDate"
              class="form-control"
              name="registrationStartDate"
              placeholder="Start date"
              @on-change="dateSelectionChanged"
            />
          </b-form-group>
        </b-col>
        <b-col v-if="selectedDateFilterId === 4" md="2" class="mt-1">
          <b-form-group label-for="example-datepicker" label="End date">
            <flat-pickr
              v-model="selectedEndDate"
              class="form-control"
              name="registrationStartDate"
              placeholder="End date"
              @on-change="dateSelectionChanged"
            />
          </b-form-group>
        </b-col>
        <b-col class="d-flex align-items-center justify-content-end fw-bold">
          <p class="fw-bolder">
            {{ formatDate(startDate) }} - {{ formatDate(endDate) }}
          </p>
        </b-col>
      </b-row>
    </b-card>
    <b-row>
      <b-col cols="6" v-if="completedCoursesChart">
        <HighchartColumn
          :key="JSON.stringify(completedCoursesChart)"
          :data="completedCoursesChart"
        />
      </b-col>
      <b-col cols="6" v-if="completedCourseByDepartmentChart">
        <HighchartColumn
          :key="JSON.stringify(completedCourseByDepartmentChart)"
          :data="completedCourseByDepartmentChart"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="6" v-if="courseSubscriberExamResultChart">
        <HighchartColumn
          :key="JSON.stringify(courseSubscriberExamResultChart)"
          :data="courseSubscriberExamResultChart"
      /></b-col>
      <b-col cols="6" v-if="courseTimeSpent">
        <HighchartColumn
          :key="JSON.stringify(courseTimeSpent)"
          :data="courseTimeSpent"
      /></b-col>
    </b-row>
    <b-card
      v-if="courseCompletePendingList && courseCompletePendingList.length > 0"
    >
      <b-card-title
        ><h3>{{ $t("Course Pending Users") }}</h3>
      </b-card-title>
      <b-row>
        <b-col cols="12" md="4" class="mb-1">
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              v-model="searchQuery"
              class="d-inline-block mr-1"
              :placeholder="$t('Search...')"
            />
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-table
            :filter="searchQuery"
            ref="refUserListTable"
            class="position-relative"
            :items="courseCompletePendingList"
            responsive
            primary-key="id"
            show-empty
            :empty-text="$t('No matching records found')"
          >
            <template #cell(courseAssignedDate)="data">
              <div class="text-nowrap">
                <span class="align-text-top text-capitalize">{{
                  formatDate(data.item.courseAssignedDate)
                }}</span>
              </div>
            </template>
          </b-table>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import {
  ref,
  onUnmounted,
  computed,
  onMounted,
  watch,
} from "@vue/composition-api";
import {
  BCardBody,
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BFormInput,
  BCardTitle,
  BFormGroup,
} from "bootstrap-vue";
import store from "@/store";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import lmsDashboardStoreModule from "./lmsDashboardStoreModule";
import HighchartColumn from "./components/HighchartColumn.vue";
import useLmsDashboard from "./useLmsDashboard";
import CoursePicker from "./components/CoursePicker.vue";

export default {
  components: {
    flatPickr,
    BCardTitle,
    HighchartColumn,
    BFormInput,
    BFormGroup,
    BCardBody,
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    CoursePicker,
    vSelect,
  },
  setup() {
    const USER_APP_STORE_MODULE_NAME = "app-lms-dashboard";
    const searchQuery = ref("");
    // Register module
    if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) {
      store.registerModule(USER_APP_STORE_MODULE_NAME, lmsDashboardStoreModule);
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(USER_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(USER_APP_STORE_MODULE_NAME);
      }
    });
    onMounted(() => {});

    const dateFilterOptions = [
      {
        id: 1,
        title: `Weekly`,
      },
      { id: 2, title: `Monthly` },
      {
        id: 3,
        title: `Yearly`,
      },
      {
        id: 4,
        title: `Custom`,
      },
    ];
    const selectedStartDate = ref(new Date());
    const selectedEndDate = ref(new Date());

    const selectedDateFilterId = ref(3);

    const coursesDropdown = computed(() => {
      return store.getters["app-lms-dashboard/getCourses"];
    });

    const selectedCourse = ref(0);

    const {
      startDate,
      endDate,
      fetchDashboardData,
      completedCoursesChart,
      completedCourseByDepartmentChart,
      courseSubscriberExamResultChart,
      courseTimeSpent,
      courseCompletePendingList,
    } = useLmsDashboard(
      selectedCourse,
      selectedDateFilterId,
      selectedStartDate,
      selectedEndDate
    );

    const courseSelectionChanged = (newVal) => {
      console.log(newVal);
      selectedCourse.value = newVal;
    };
    const dateSelectionChanged = () => {
      fetchDashboardData();
    };
    watch(selectedDateFilterId, (newVal) => {
      fetchDashboardData();
    });
    function formatDate(dateString) {
      if (!dateString) {
        return "";
      }
      const date = new Date(dateString);
      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      };

      const formattedDate = new Intl.DateTimeFormat("en-US", options).format(
        date
      );
      return formattedDate.replace(",", "");
    }

    return {
      startDate,
      endDate,
      formatDate,
      searchQuery,
      courseCompletePendingList,
      courseSelectionChanged,
      dateSelectionChanged,
      completedCoursesChart,
      completedCourseByDepartmentChart,
      courseSubscriberExamResultChart,
      dateFilterOptions,
      selectedDateFilterId,
      coursesDropdown,
      courseTimeSpent,
      selectedCourse,
      selectedStartDate,
      selectedEndDate,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
